/**
 * The render loop.
 *
 * ONE rAF loop drives every layer. A naive implementation starts a loop per
 * effect; three layers then means three loops each clearing the same canvas.
 *
 * `dt` is clamped. A backgrounded tab, a breakpoint, or a slow first frame
 * otherwise delivers a multi-second delta and every particle teleports off
 * screen — the field visibly "jumps" when a shopper returns to the tab, which
 * looks like a bug and is trivially avoidable.
 */

import { clamp } from './budget.ts';
import type { Governor } from './governor.ts';
import type { Stage } from './stage.ts';
import type { Atlas, Family, Particle, StepContext } from './types.ts';

export interface Layer {
  family: Family;
  particles: Particle[];
  /**
   * How many of `particles` are actually stepped and drawn.
   *
   * The pool is allocated ONCE at the device's tier and never resized — the
   * governor moves this number instead. Reallocating would mean re-spawning
   * every particle, and the field would visibly restart every time the device
   * got slightly busier.
   */
  active?: number;
  atlas: Atlas;
  spriteCount: number;
  /** 0..1 master alpha for the whole layer. */
  alpha: number;
}

export interface LoopOptions {
  stage: Stage;
  layers: Layer[];
  wind: number;
  speed: number;
  rnd: () => number;
  /** Called once per frame after drawing, for the FPS meter in the admin. */
  onFrame?: (fps: number) => void;
  /**
   * The adaptive frame budget. Omit to run every particle regardless of cost —
   * which is what the ADMIN preview wants: a merchant judging an effect should
   * see the effect they configured, not a quietly thinned version of it.
   */
  governor?: Governor;
}

export interface Loop {
  start(): void;
  stop(): void;
  /** True while rAF is scheduled. */
  readonly running: boolean;
  destroy(): void;
}

export function createLoop(opts: LoopOptions): Loop {
  const { stage, layers } = opts;
  let raf = 0;
  let last = 0;
  let t = 0;
  let running = false;
  let fpsAcc = 0;
  let fpsFrames = 0;

  const ctx: StepContext = {
    dt: 0,
    w: stage.w,
    h: stage.h,
    wind: opts.wind,
    speed: opts.speed,
    t: 0,
  };

  function frame(now: number) {
    if (!running) return;
    // ⚠ Wall time spent in THIS callback, not the gap between callbacks. See
    // the header of governor.ts: rAF is pinned to the display, so `dt` reads
    // ~16.7 ms whether our work took 1 ms or 15, and is saturated exactly where
    // the decision matters.
    const workStart = opts.governor ? performance.now() : 0;

    // Clamp to 50ms (20fps floor). Anything slower is a stall, not a slow frame,
    // and integrating it would teleport the field.
    const dt = last === 0 ? 1 / 60 : clamp((now - last) / 1000, 0, 0.05);
    last = now;
    t += dt;

    ctx.dt = dt;
    ctx.t = t;
    ctx.w = stage.w;
    ctx.h = stage.h;

    stage.ctx.clearRect(0, 0, stage.w, stage.h);

    for (const layer of layers) {
      const { particles, family, atlas, spriteCount } = layer;
      const img = atlas.canvas as CanvasImageSource;

      // The whole-canvas pass runs first and in CSS pixels: the transform is
      // still identity-plus-dpr here, before any particle has replaced it.
      if (family.overlay) {
        stage.ctx.globalAlpha = layer.alpha;
        family.overlay(stage.ctx as CanvasRenderingContext2D, ctx);
        stage.ctx.setTransform(stage.dpr, 0, 0, stage.dpr, 0, 0);
        stage.ctx.globalAlpha = 1;
      }

      const live = layer.active ?? particles.length;
      for (let i = 0; i < live; i++) {
        const p = particles[i];
        if (!family.step(p, ctx)) {
          family.spawn(p, ctx, opts.rnd);
          continue;
        }

        const cellIdx = (p.tint % atlas.tints) * spriteCount + (p.cell % spriteCount);
        const cell = atlas.cells[cellIdx];
        if (!cell) continue;

        const a = p.alpha * layer.alpha;
        if (a <= 0.01) continue;

        stage.ctx.globalAlpha = a;

        // ⚠ THE DPR MUST BE BAKED INTO setTransform, not applied after it.
        //
        // `setTransform` REPLACES the matrix, so its e/f translation is in DEVICE
        // pixels. Writing `setTransform(cos, sin, -sin, cos, p.x, p.y)` and then
        // `scale(dpr, dpr)` puts a particle at CSS y=300 at device y=300 — which
        // on a 2x display is CSS y=150. Every particle lands at half its
        // position and the bottom half of the canvas is unreachable.
        //
        // Invisible at dpr=1, which is why the first headless render (taken at
        // deviceScaleFactor: 1) looked correct. It is wrong on essentially every
        // real merchant laptop and every phone.
        const d = stage.dpr;
        const cos = Math.cos(p.r);
        const sin = Math.sin(p.r);
        stage.ctx.setTransform(d * cos, d * sin, -d * sin, d * cos, p.x * d, p.y * d);
        const half = p.size / 2;
        stage.ctx.drawImage(img, cell.x, cell.y, cell.w, cell.h, -half, -half, p.size, p.size);
      }
    }

    // Reset for the next clearRect.
    stage.ctx.setTransform(stage.dpr, 0, 0, stage.dpr, 0, 0);
    stage.ctx.globalAlpha = 1;

    if (opts.governor) {
      const scale = opts.governor.observe(performance.now() - workStart);
      for (const layer of layers) {
        /**
         * At least one particle per layer — but never more than the layer HAS.
         *
         * ⚠ `Math.max(1, …)` alone is a crash. FLASH is a luminance change
         * across the whole canvas with `baseCount: 0`, so its pool is legitimately
         * EMPTY; the floor then claimed one particle that does not exist and the
         * next frame dereferenced `particles[0].tint`. Because `createLoop`
         * schedules the next rAF at the END of the frame, that exception stops
         * the chain — killing every layer on the page, not just this one.
         *
         * Found by the frame-budget harness, which reported it as "0 frames
         * sampled" until it was taught to surface page errors. No test caught it:
         * the engine tests never build a loop, and the admin preview refuses to
         * mount a family with no sprites.
         */
        const n = layer.particles.length;
        layer.active = n === 0 ? 0 : Math.min(n, Math.max(1, Math.round(n * scale)));
      }
    }

    if (opts.onFrame) {
      fpsAcc += dt;
      fpsFrames++;
      if (fpsAcc >= 0.5) {
        opts.onFrame(fpsFrames / fpsAcc);
        fpsAcc = 0;
        fpsFrames = 0;
      }
    }

    raf = requestAnimationFrame(frame);
  }

  return {
    get running() {
      return running;
    },
    start() {
      if (running) return;
      running = true;
      last = 0; // force a synthetic first dt rather than a huge one
      raf = requestAnimationFrame(frame);
    },
    stop() {
      running = false;
      if (raf) cancelAnimationFrame(raf);
      raf = 0;
    },
    destroy() {
      this.stop();
      layers.length = 0;
    },
  };
}

/** Allocate a particle pool. Fields are seeded so the shape is monomorphic. */
export function createPool(n: number): Particle[] {
  const pool: Particle[] = new Array(n);
  for (let i = 0; i < n; i++) {
    pool[i] = {
      x: 0,
      y: 0,
      vx: 0,
      vy: 0,
      r: 0,
      vr: 0,
      size: 1,
      depth: 0,
      cell: 0,
      tint: 0,
      alpha: 1,
      age: 0,
      phase: 0,
    };
  }
  return pool;
}
